import { UserDisplay } from "@coaching-culture/types";
import {
  Box,
  Circle,
  DatePill,
  EmptyState,
  Flex,
  Label,
  Loader,
  Panel,
  Table,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { useGoalTerminology } from "auth/OrgProvider";
import { ScrollableContainer } from "components/ScrollableContainer";
import { Toggle } from "components/Toggle";
import { differenceInDays, fromUnixTime } from "date-fns";
import { keyBy, sortBy } from "lodash";
import { useApplicableCollectiveGoals, useTeam } from "queries/performance";
import { FaBuilding, FaMedal, FaStar } from "react-icons/fa";
import { useLocalStorage } from "react-use";
import { WidgetPanelHeader } from "./WidgetPanelHeader";

const isNewGoal = (date: number) =>
  differenceInDays(new Date(), fromUnixTime(date)) < 30;

export const TeamGoalWidget = () => {
  const data = useApplicableCollectiveGoals();
  const team = useTeam();
  const [user] = useUser();
  const terms = useGoalTerminology();

  const [showAll, setShowAll] = useLocalStorage("team-goal-all", false);

  const toShow = showAll
    ? [...data.teamGoals, ...data.orgGoals]
    : data.teamGoals;

  const lms: Record<string, UserDisplay> = keyBy(
    team.data?.lineManagers ?? [],
    (x) => x.id,
  );

  lms[user.id] = user;

  return (
    <Panel width={"100%"}>
      <WidgetPanelHeader title={`Collective ${terms.goal.asPluralBody()}`}>
        <Toggle
          leftLabel={`Team ${terms.goal.asPluralBody()}`}
          rightLabel="All"
          value={showAll}
          onChange={setShowAll}
        />
      </WidgetPanelHeader>
      {!data.isFetched ? (
        <Loader />
      ) : toShow.length === 0 ? (
        <EmptyState compact icon={FaMedal}>
          No collective {terms.goal.asPluralBody()} yet.
        </EmptyState>
      ) : (
        <ScrollableContainer maxHeight={310}>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 200 }}>Owner</th>
                <th>{terms.goal.asTitle()}</th>
                <th style={{ width: 240 }}>Date set</th>
              </tr>
            </thead>
            <tbody>
              {sortBy(toShow, (x) => x.createdOn)
                .reverse()
                .map((x) => (
                  <tr>
                    <td>
                      {"lineManagerId" in x ? (
                        <UserFlag
                          user={lms[x.lineManagerId]}
                          isYou={x.lineManagerId === user.id}
                        />
                      ) : (
                        <Flex alignItems="center">
                          <Circle color="positive" icon={FaBuilding} />
                          <Box ml={2}>
                            <Text fontWeight={500}>Organisation</Text>
                            <Text fontSize="caption" color="textMuted">
                              Org level {terms.goal.asBody()}
                            </Text>
                          </Box>
                        </Flex>
                      )}
                    </td>
                    <td>{x.name}</td>
                    <td>
                      <DatePill dateOnly value={x.createdOn} />
                      {isNewGoal(x.createdOn) && (
                        <Label size="small" color="primary" icon={FaStar}>
                          New
                        </Label>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </ScrollableContainer>
      )}
    </Panel>
  );
};
